import { useEffect } from 'react';

const terminologyConfig = {
  storeLabel: process.env.REACT_APP_TERM_STORE_LABEL || '社別',
  memberLabel: process.env.REACT_APP_TERM_MEMBER_LABEL || '社友',
  allClubsLabel: process.env.REACT_APP_TERM_ALL_CLUBS_LABEL || '各社',
  localClubLabel: process.env.REACT_APP_TERM_LOCAL_CLUB_LABEL || '本社',
  allyClubLabel: process.env.REACT_APP_TERM_ALLY_CLUB_LABEL || '友社',
  internalClubLabel: process.env.REACT_APP_TERM_INTERNAL_CLUB_LABEL || '社內',
  menuVersionLabel: process.env.REACT_APP_MENU_VERSION_LABEL || 'IMC3.0',
};

const replacementRules = [
  { pattern: /IMC\s*3\.0/g, replacement: terminologyConfig.menuVersionLabel },
  { pattern: /IMC3\.0/g, replacement: terminologyConfig.menuVersionLabel },
  { pattern: /各社/g, replacement: terminologyConfig.allClubsLabel },
  { pattern: /本社/g, replacement: terminologyConfig.localClubLabel },
  { pattern: /友社/g, replacement: terminologyConfig.allyClubLabel },
  { pattern: /社內/g, replacement: terminologyConfig.internalClubLabel },
  { pattern: /社別/g, replacement: terminologyConfig.storeLabel },
  { pattern: /社友/g, replacement: terminologyConfig.memberLabel },
];

export function replaceTerminology(text) {
  if (typeof text !== 'string' || !text) return text;

  return replacementRules.reduce(
    (current, rule) => current.replace(rule.pattern, rule.replacement),
    text
  );
}

function rewriteAttribute(element, attributeName) {
  const currentValue = element.getAttribute(attributeName);
  if (!currentValue) return;

  const replacedValue = replaceTerminology(currentValue);
  if (replacedValue !== currentValue) {
    element.setAttribute(attributeName, replacedValue);
  }
}

function applyTerminologyToDocument() {
  if (typeof document === 'undefined' || !document.body) return;

  document.title = replaceTerminology(document.title);

  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) {
          return NodeFilter.FILTER_REJECT;
        }

        const parent = node.parentElement;
        if (!parent) {
          return NodeFilter.FILTER_REJECT;
        }

        if (['SCRIPT', 'STYLE', 'NOSCRIPT'].includes(parent.tagName)) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    }
  );

  const textUpdates = [];
  let currentNode = walker.nextNode();

  while (currentNode) {
    const replacedValue = replaceTerminology(currentNode.nodeValue);
    if (replacedValue !== currentNode.nodeValue) {
      textUpdates.push([currentNode, replacedValue]);
    }
    currentNode = walker.nextNode();
  }

  textUpdates.forEach(([node, value]) => {
    node.nodeValue = value;
  });

  document.querySelectorAll('[placeholder], [title], [aria-label]').forEach((element) => {
    rewriteAttribute(element, 'placeholder');
    rewriteAttribute(element, 'title');
    rewriteAttribute(element, 'aria-label');
  });
}

export function useTerminologyDomRewriter() {
  useEffect(() => {
    if (typeof document === 'undefined' || !document.body) return undefined;

    let scheduled = false;
    const scheduleRewrite = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        applyTerminologyToDocument();
      });
    };

    applyTerminologyToDocument();

    const observer = new MutationObserver(() => {
      scheduleRewrite();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['placeholder', 'title', 'aria-label'],
    });

    return () => observer.disconnect();
  }, []);
}

export { terminologyConfig };
